/**
 * Theme preference — Auto (follows the system, default), Light « Crystal », Dark « Midnight ».
 * Remembered on the device (and on the account by the apps), applied before the first paint
 * (NO_FLASH_SCRIPT), switched with a circular reveal from the button (P0-08).
 */

import { accentCss, motion } from "@agencyos/design-tokens";
import { useSyncExternalStore } from "react";

export type ThemePreference = "auto" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

import { THEME_STORAGE_KEY } from "./no-flash";

export { THEME_STORAGE_KEY };

const listeners = new Set<() => void>();

function read(): ThemePreference {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    return saved === "light" || saved === "dark" ? saved : "auto";
  } catch {
    return "auto"; // private mode or blocked storage: never crash
  }
}

function write(preference: ThemePreference): void {
  try {
    if (preference === "auto") window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Preference simply not remembered on this device.
  }
}

const media = (query: string): MediaQueryList | null =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(query)
    : null;

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== "auto") return preference;
  return media("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

let current: ThemePreference = typeof window === "undefined" ? "auto" : read();

media("(prefers-color-scheme: dark)")?.addEventListener("change", () => {
  if (current === "auto") {
    applyTheme(resolveTheme("auto"));
    listeners.forEach((listener) => listener());
  }
});

function prefersReducedMotion(): boolean {
  return Boolean(media("(prefers-reduced-motion: reduce)")?.matches);
}

/** Circular reveal from `origin` (400 ms); instant when motion is reduced or unsupported. */
function transitionTo(theme: ResolvedTheme, origin?: { x: number; y: number }): void {
  if (typeof document.startViewTransition !== "function" || !origin || prefersReducedMotion()) {
    applyTheme(theme);
    return;
  }
  const radius = Math.hypot(
    Math.max(origin.x, window.innerWidth - origin.x),
    Math.max(origin.y, window.innerHeight - origin.y),
  );
  const transition = document.startViewTransition(() => applyTheme(theme));
  void transition.ready.then(() => {
    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${origin.x}px ${origin.y}px)`,
          `circle(${radius}px at ${origin.x}px ${origin.y}px)`,
        ],
      },
      {
        duration: motion.themeTransition,
        easing: `cubic-bezier(${motion.easeOutExpo.join(",")})`,
        pseudoElement: "::view-transition-new(root)",
      },
    );
  });
}

export function setThemePreference(
  preference: ThemePreference,
  origin?: { x: number; y: number },
): void {
  current = preference;
  write(preference);
  transitionTo(resolveTheme(preference), origin);
  listeners.forEach((listener) => listener());
}

export function getThemePreference(): ThemePreference {
  return current;
}

export function useThemePreference(): [ThemePreference, typeof setThemePreference] {
  const preference = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => "auto" as ThemePreference,
  );
  return [preference, setThemePreference];
}

interface NavigatorWithHints extends Navigator {
  deviceMemory?: number;
  connection?: { saveData?: boolean };
}

/** Low-end device, Save-Data or reduced transparency → opaque surfaces (§2 bis, P0-09). */
export function shouldDisableGlass(nav: NavigatorWithHints = navigator): boolean {
  const lowMemory = nav.deviceMemory !== undefined && nav.deviceMemory <= 2;
  const fewCores = nav.hardwareConcurrency !== undefined && nav.hardwareConcurrency <= 2;
  const saveData = nav.connection?.saveData === true;
  const reduced = Boolean(media("(prefers-reduced-transparency: reduce)")?.matches);
  return lowMemory || fewCores || saveData || reduced;
}

/**
 * Replace the accent with the agency's charter colour (both themes, AA-corrected by
 * deriveAccent). `null` goes back to the default accent.
 */
export function applyBrandAccent(brandHex: string | null): void {
  if (typeof document === "undefined") return;
  const id = "ao-brand-accent";
  const existing = document.getElementById(id);
  if (!brandHex || !/^#[0-9a-fA-F]{6}$/.test(brandHex)) {
    existing?.remove();
    return;
  }
  const style = existing ?? Object.assign(document.createElement("style"), { id });
  style.textContent = accentCss(brandHex);
  if (!existing) document.head.append(style);
}

export { NO_FLASH_SCRIPT } from "./no-flash";
